import { useState } from 'react';
import {
  AccountForm,
  NotificationPrefsForm,
  ProfileForm,
  useNotificationPrefs,
  useProfile,
  type NotificationPrefs,
} from '@/features/profile';
import { ErrorState, Skeleton } from '@/shared/ui';
import { cn } from '@/shared/lib/utils/cn';
import { Seo } from '@/shared/seo';

const TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'account', label: 'Account' },
  { id: 'notifications', label: 'Notifications' },
] as const;
type TabId = (typeof TABS)[number]['id'];

const DEFAULT_PREFS: NotificationPrefs = {
  reply: true,
  newsletter: true,
  newArticle: true,
  weeklyDigest: false,
};

function FormSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-10 w-full rounded-xl" />
      <Skeleton className="h-10 w-full rounded-xl" />
      <Skeleton className="h-24 w-full rounded-xl" />
    </div>
  );
}

export function ProfilePage() {
  const [tab, setTab] = useState<TabId>('profile');
  const profileQuery = useProfile();
  const prefsQuery = useNotificationPrefs();

  return (
    <div className="mx-auto max-w-2xl px-6 py-14">
      {/* Behind auth — nothing here belongs in an index. */}
      <Seo title="Your account" noIndex />
      <h1 className="mb-8 font-display text-4xl font-black italic tracking-tight text-foreground">
        Your account
      </h1>

      <div className="mb-8 flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              '-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              tab === t.id
                ? 'border-amber-400 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'profile' &&
        (profileQuery.isPending ? (
          <FormSkeleton />
        ) : profileQuery.isError || !profileQuery.data ? (
          <ErrorState title="Couldn’t load your profile" onRetry={() => void profileQuery.refetch()} />
        ) : (
          <ProfileForm profile={profileQuery.data} />
        ))}

      {tab === 'account' && <AccountForm />}

      {tab === 'notifications' &&
        (prefsQuery.isPending ? (
          <FormSkeleton />
        ) : prefsQuery.isError ? (
          <ErrorState title="Couldn’t load preferences" onRetry={() => void prefsQuery.refetch()} />
        ) : (
          <NotificationPrefsForm prefs={prefsQuery.data ?? DEFAULT_PREFS} />
        ))}
    </div>
  );
}
